import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Collapse, Dropdown, Empty, Input, List, Tag, Typography } from 'antd';

import { buildActionMenu } from '../common/actionMenu';
import { confirmDestructive } from '../common/confirmDestructive';
import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';
import { listImports, previewImportUndo, undoImport } from '../../services/imports';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { formatCount } from '../../utils/counts';
import { IMPORT_LABEL, importPath } from '../../utils/importEntry';
import { ARROW_COUNTERCLOCKWISE, KEBAB } from '../icons';
import ChangeCardTaskRow from './ChangeCardTaskRow';

const { Text } = Typography;

const PAGE = 20;
// How often the list is read again while an undo, or a card change task
// (KPR-705), runs in the background.
const POLL_MS = 4000;

/** A card change running as a background task, listed beside the imports (KPR-705). */
const isTask = row => row?.kind === 'changeCard';

// Why an item stays when its import is undone (keepr-api docs/SCHEMA.md § Imports).
export const KEPT_REASON_WORDS = {
	changed_since: 'Changed since the import',
	linked: 'Another record links to it',
	never_delete: 'Its card never deletes records',
	locked: 'The record is locked',
	not_revertable: 'Its change can’t be undone',
	no_access: 'You can’t edit it',
	still_driven: 'A calculation sets its value',
	moved: 'It is in another collection now',
	gone: 'Someone deleted it already',
	refused: 'keepr refused to change it back',
};

// What an undo leaves in place, by kind.
function leftBehindWords( { kind, count } ) {
	if ( kind === 'elsewhere' ) return `${ formatCount( count, 'change' ) } its rules made in other collections`;
	if ( kind === 'item.delete' ) return `${ formatCount( count, 'item' ) } its rules deleted`;
	if ( kind === 'sequence_numbers' ) return `${ formatCount( count, 'number' ) } the deleted items used — numbers are never reissued`;
	return `${ formatCount( count, 'other change' ) } (${ kind })`;
}

const WHY_WORDS = {
	undone: 'Undone',
	no_request_id: 'Recorded before imports could be undone',
	nothing_written: 'Nothing was written',
};

/** "Delete 348 items and restore 3. 2 will stay." */
export function describeUndo( plan ) {
	const doing = [
		plan.delete ? `delete ${ formatCount( plan.delete, 'item' ) }` : null,
		plan.restore ? `restore ${ formatCount( plan.restore, 'item' ) } to how ${ plan.restore === 1 ? 'it was' : 'they were' }` : null,
	].filter( Boolean ).join( ' and ' );
	const kept = plan.keptCount || 0;
	const sentence = doing ? `${ doing.charAt( 0 ).toUpperCase() }${ doing.slice( 1 ) }.` : 'Nothing is left to undo.';
	return kept ? `${ sentence } ${ formatCount( kept, 'item' ) } will stay.` : sentence;
}

// `runCount` from keepr-api; `runIds` (the last 100) before it.
const batchesOf = row => row?.runCount ?? row?.runIds?.length ?? 0;

function whoOf( row ) {
	const by = row?.by || {};
	const name = by.name || 'Someone';
	return by.keyName ? `${ name } · key “${ by.keyName }”` : name;
}

function PlanDetails( { plan } ) {
	const kept = Array.isArray( plan.kept ) ? plan.kept : [];
	const left = Array.isArray( plan.leftBehind ) ? plan.leftBehind : [];
	if ( ! kept.length && ! left.length ) return null;
	const items = [];
	if ( kept.length ) {
		items.push( {
			key: 'kept',
			label: `What stays (${ plan.keptCount || kept.length })`,
			children: (
				<ul className='imports-plan__list'>
					{kept.map( entry => (
						<li key={entry.itemId}>
							<Text>{entry.title || 'Untitled item'}</Text>
							<Text type='secondary'>{` — ${ KEPT_REASON_WORDS[ entry.reason ] || entry.reason }`}</Text>
						</li>
					) )}
					{( plan.keptCount || 0 ) > kept.length ? (
						<li><Text type='secondary'>{`and ${ plan.keptCount - kept.length } more`}</Text></li>
					) : null}
				</ul>
			),
		} );
	}
	if ( left.length ) {
		items.push( {
			key: 'left',
			label: 'Not undone',
			children: (
				<ul className='imports-plan__list'>
					{left.map( entry => <li key={entry.kind}><Text>{leftBehindWords( entry )}</Text></li> )}
					<li><Text type='secondary'>Messages its rules sent are not recalled.</Text></li>
				</ul>
			),
		} );
	}
	return <Collapse size='small' ghost items={items} className='imports-plan__details' />;
}

/**
 * Imports tab (managers only; CollectionForm gates the tab and the API's
 * routes are session-only and manage). One row per import a person, an
 * assistant or a script made; Import a spreadsheet (KPR-1899) is the door to a
 * new one. ⋯ → Undo import previews first, then asks in red with the
 * counts, and says what stayed. A card change too large for its request runs
 * as a background task and is a row here too (KPR-705, ChangeCardTaskRow),
 * with its progress while it runs; it has no Undo import, but its own Undo
 * card change (KPR-704), whose progress this list follows too.
 */
export default function ImportsTab( { collectionId } ) {
	const { modal, message } = App.useApp();
	const [ rows, setRows ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ loadingMore, setLoadingMore ] = useState( false );
	const [ hasMore, setHasMore ] = useState( false );
	const [ error, setError ] = useState( null );
	// The first read has answered: until then the tab is a skeleton, after it the tab keeps its data.
	const [ loaded, setLoaded ] = useState( false );
	const [ busy, setBusy ] = useState( null );

	// { importKey, requestId?, since } for an undo this tab started that
	// keepr is finishing in the background (a 202, or a request that timed out).
	const watching = useRef( null );

	// `quiet` reloads (while an undo runs) keep the list on screen.
	const load = useCallback( async ( { quiet = false } = {} ) => {
		if ( ! quiet ) setLoading( true );
		setError( null );
		try {
			const page = await listImports( collectionId, { limit: PAGE } );
			setRows( page );
			setHasMore( page.length === PAGE );
			setLoaded( true );
		} catch ( e ) {
			if ( ! quiet ) setError( e );
		} finally {
			if ( ! quiet ) setLoading( false );
		}
	}, [ collectionId ] );

	useEffect( () => { load(); }, [ load ] );

	// While an undo runs, read the list again until it is stamped — and say
	// how it ended when it was this tab that started it.
	useEffect( () => {
		const watch = watching.current;
		if ( watch ) {
			const row = rows.find( r => r.importKey === watch.importKey );
			const ended = row && row.undone && ( watch.requestId ? row.undone.requestId === watch.requestId : new Date( row.undone.at ) >= watch.since );
			if ( ended ) {
				watching.current = null;
				const kept = row.undone.kept || 0;
				message.success( `Undid the import: ${ formatCount( row.undone.deleted || 0, 'item' ) } deleted, ${ row.undone.restored || 0 } restored${ kept ? `, ${ kept } kept` : '' }.` );
			} else if ( row && ! row.undoing && Date.now() - watch.since > POLL_MS * 2 ) {
				watching.current = null;
				message.error( 'The undo stopped before it finished. Choose Undo import again to finish it; nothing is undone twice.' );
			}
		}
		if ( ! watching.current && ! rows.some( r => r.undoing || ( isTask( r ) && ( r.changeCard?.running || r.changeCard?.undo?.running ) ) ) ) return undefined;
		const timer = setTimeout( () => load( { quiet: true } ), POLL_MS );
		return () => clearTimeout( timer );
	}, [ rows, load, message ] );

	const loadMore = async () => {
		const last = rows[ rows.length - 1 ];
		if ( ! last ) return;
		setLoadingMore( true );
		try {
			const page = await listImports( collectionId, { limit: PAGE, before: new Date( last.lastAt ).toISOString() } );
			setRows( prev => [ ...prev, ...page ] );
			setHasMore( page.length === PAGE );
		} catch ( e ) {
			message.error( e?.response?.data?.message || 'Unable to load older imports.' );
		} finally {
			setLoadingMore( false );
		}
	};

	const runUndo = async ( row, reason ) => {
		setBusy( row.importKey );
		const since = new Date();
		try {
			const done = await undoImport( collectionId, row.importKey, { reason } );
			if ( done.started ) {
				// A large undo: keepr answered with the plan and is finishing it.
				watching.current = { importKey: row.importKey, requestId: done.requestId, since };
				message.info( `Undoing the import: ${ formatCount( done.delete || 0, 'item' ) } to delete. A large one takes a few minutes; this list updates when it is done.` );
			} else {
				const kept = done.keptCount || 0;
				message.success( `Undid the import: ${ formatCount( done.deleted || 0, 'item' ) } deleted, ${ done.restored || 0 } restored${ kept ? `, ${ kept } kept` : '' }.` );
			}
			await load( { quiet: true } );
		} catch ( e ) {
			const status = e?.response?.status;
			if ( ! status || status >= 500 ) {
				// No answer in time is not "nothing happened": keepr may still
				// be undoing it. Follow the list instead of guessing.
				watching.current = { importKey: row.importKey, since };
				message.warning( 'keepr did not answer in time. The undo may still be running; this list updates when it finishes.' );
			} else {
				message.error( e?.response?.data?.message || 'The import could not be undone.' );
			}
			await load( { quiet: true } );
		} finally {
			setBusy( null );
		}
	};

	const startUndo = async ( row ) => {
		setBusy( row.importKey );
		let plan;
		try {
			plan = await previewImportUndo( collectionId, row.importKey );
		} catch ( e ) {
			message.error( e?.response?.data?.message || 'Unable to check what undoing this import would do.' );
			setBusy( null );
			return;
		}
		setBusy( null );
		if ( ! plan.delete && ! plan.restore ) {
			modal.info( {
				title: 'Nothing to undo',
				content: (
					<div className='imports-plan'>
						<p>{describeUndo( plan )}</p>
						<PlanDetails plan={plan} />
					</div>
				),
			} );
			return;
		}
		// Modal.confirm content is not re-rendered, so the reason is read
		// from the field's last change rather than from state.
		let reason = '';
		confirmDestructive( modal, {
			title: 'Undo this import?',
			okText: 'Undo import',
			width: 520,
			content: (
				<div className='imports-plan'>
					<p>{describeUndo( plan )}</p>
					{plan.ruleWrites ? (
						<p className='imports-plan__note'>{`Includes ${ formatCount( plan.ruleWrites, 'item' ) } the collection’s rules wrote during the import.`}</p>
					) : null}
					{plan.needsReason ? (
						<label className='imports-plan__reason'>
							<span className='micro-label'>Reason</span>
							<Input.TextArea
								rows={2}
								maxLength={500}
								placeholder='Undid an import'
								onChange={e => { reason = e.target.value; }}
							/>
							<Text type='secondary'>Kept with each deleted item; some of this collection’s cards ask for one.</Text>
						</label>
					) : null}
					<PlanDetails plan={plan} />
				</div>
			),
			onOk: () => runUndo( row, reason ),
		} );
	};

	const menuFor = row => buildActionMenu( [
		{
			key: 'undo',
			group: 'destructive',
			icon: <ARROW_COUNTERCLOCKWISE />,
			label: 'Undo import…',
			disabled: ! row.undoable || busy === row.importKey || Boolean( row.undoing ),
			title: row.undoing ? 'An undo of this import is running.' : ( row.undoable ? undefined : WHY_WORDS[ row.why ] ),
			onClick: () => startUndo( row ),
		},
	] );

	if ( ! loaded ) {
		return (
			<TabLoadState
				loading={loading}
				error={error}
				label='imports'
				errorDetail='Imports are for managers of this collection. If your access just changed, that may be why.'
				onRetry={() => load()}
				rows={4}
			/>
		);
	}

	return (
		<div className='settings-column imports-tab'>
			{error ? <RefreshFailedNotice label='imports' onRetry={() => load()} /> : null}
			<section className='form-panel'>
				<div className='form-panel__kicker-row'>
					<div className='micro-label form-panel__kicker'>Imports</div>
					<Link to={importPath( collectionId )}><Button size='small'>{IMPORT_LABEL}</Button></Link>
				</div>
				<Text type='secondary' className='form-panel__hint'>
					Undoing an import deletes the items it added and puts back the ones it changed. Anything changed since stays. A change of card for more than 100 items runs here too, with its progress, and is undone from here.
				</Text>
				<List
					className='imports-list'
					dataSource={rows}
					rowKey={row => row.importKey}
					locale={{ emptyText: (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description='Imports appear here when you import a spreadsheet, or an assistant or a script adds items in batches. Large card changes do too.'
						/>
					) }}
					renderItem={row => {
						if ( isTask( row ) ) {
							return (
								<List.Item key={row.importKey} className='imports-list__item imports-list__item--task'>
									<div className='imports-list__row'>
										<ChangeCardTaskRow row={row} collectionId={collectionId} who={whoOf( row )} onChanged={() => load( { quiet: true } )} />
									</div>
								</List.Item>
							);
						}
						const menu = menuFor( row );
						const counts = [
							row.created ? `${ row.created } added` : null,
							row.updated ? `${ row.updated } changed` : null,
							row.failed ? `${ row.failed } refused` : null,
						].filter( Boolean ).join( ' · ' ) || 'Nothing written';
						return (
							<List.Item key={row.importKey} className='imports-list__item'>
								<div className='imports-list__row'>
									<div className='imports-list__main'>
										<div className='imports-list__name'>
											<Text strong>{whoOf( row )}</Text>
											{row.undoing ? <Tag>Undoing…</Tag> : null}
											{row.undone && ! row.undoing ? <Tag>{`Undone ${ formatDisplayDateTime( row.undone.at ) }`}</Tag> : null}
										</div>
										<Text type='secondary' className='imports-list__line'>
											{formatDisplayDateTime( row.lastAt )}
											{batchesOf( row ) > 1 ? ` · ${ batchesOf( row ) } batches` : ''}
										</Text>
										<Text className='imports-list__line'>{counts}</Text>
										{! row.undoable && row.why && row.why !== 'undone' ? (
											<Text type='secondary' className='imports-list__line'>{WHY_WORDS[ row.why ]}</Text>
										) : null}
										{row.importId ? (
											<Text type='secondary' className='imports-list__line imports-list__id'>{row.importId}</Text>
										) : null}
									</div>
									{menu ? (
										<Dropdown trigger={[ 'click' ]} placement='bottomRight' menu={menu}>
											<Button
												icon={<KEBAB />}
												loading={busy === row.importKey}
												aria-label={`Actions for the import of ${ formatDisplayDateTime( row.lastAt ) }`}
											/>
										</Dropdown>
									) : null}
								</div>
							</List.Item>
						);
					}}
				/>
				{hasMore ? (
					<Button onClick={loadMore} loading={loadingMore} className='imports-list__more'>Show older imports</Button>
				) : null}
			</section>
		</div>
	);
}
